/**
 * Renderer Capability Manifest (v1)
 *
 * Single source of truth for what the DynamicGameRenderer can play.
 * Edge functions inject this into the AI prompt so generated GameSpecs
 * are guaranteed to be engine-compatible.
 */

export const SCENE_TYPES = [
  'mcq',
  'fill_blank',
  'flashcard',
  'matching',
  'ordering',
  'drag_to_bucket',
  'number_line',
  'worked_steps',
  'reading',
  'sprint',
  'tap_correct',
  'memory_pairs',
  'sequence_input',
] as const;

export type SceneType = (typeof SCENE_TYPES)[number];

export const THEMES = [
  'space',
  'jungle',
  'underwater',
  'classroom',
  'sports',
] as const;

export type Theme = (typeof THEMES)[number];

export const GAME_ENGINE = {
  spec_version: 1 as const,
  engine_version: 1 as const,
  scene_types: SCENE_TYPES,
  themes: THEMES,
  limits: {
    scenes_min: 3,
    scenes_max: 15,
    text_field_max_chars: 280,
    title_max_chars: 80,
    description_max_chars: 240,
    options_min: 2,
    options_max: 6,
    matching_pairs_max: 8,
    memory_pairs_max: 10,
    ordering_items_max: 8,
    bucket_count_max: 5,
    sprint_questions_max: 30,
    estimated_minutes_max: 30,
    lives_max: 9,
    time_limit_seconds_max: 1800,
  },
  forbidden: {
    html_tags: true,
    external_urls: true,
    code_fences: true,
    images: true, // v1 — only theme-driven visuals
  },
} as const;

export type GameEngineManifest = typeof GAME_ENGINE;
